import React, { useRef, useEffect } from 'react';
import Text from '@amzn/meridian/text';
import { DotLoader } from './DotLoader';
import { type ChatMessageProps } from './types';

const cursorStyle: React.CSSProperties = {
  display: 'inline',
  animation: 'blink 1s step-end infinite',
  color: '#8c8c8c',
  fontWeight: 300,
  fontSize: '0.85em',
};

/**
 * Renders tool call state:
 * - While input is streaming/available (tool executing): shows the animated three-dot
 *   DotLoader + tool name
 * - While output is available and preliminary (sub-agent streaming): shows live text with blinking cursor
 */
export const ToolIndicator: React.FC<ChatMessageProps> = ({ part, index }) => {
  const state = part.state as string | undefined;
  const isPreliminary = state === 'output-available' && part.preliminary === true;
  const isRunning = state === 'input-streaming' || state === 'input-available';
  const scrollRef = useRef<HTMLDivElement>(null);
  const text = isPreliminary && typeof part.output === 'string' ? part.output : '';

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [text]);

  // Sub-agent streaming: show accumulating text with blinking cursor
  if (isPreliminary) {
    if (!text.trim()) return null;
    return (
      <div key={index} style={{ marginTop: '8px' }}>
        <style>{'@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }'}</style>
        <div ref={scrollRef} style={{ whiteSpace: 'pre-wrap', maxHeight: '200px', overflow: 'auto' }}>
          <Text color="secondary" tag="span" type="b200">
            {text}
          </Text>
          <span style={cursorStyle}>▌</span>
        </div>
      </div>
    );
  }

  // Tool waiting to execute: show the animated dots + tool name
  if (!isRunning) return null;
  const toolName = (part.toolName as string) || (part.type as string)?.replace('tool-', '') || 'tool';
  const label = `${toolName}...`;
  return (
    <div key={index} style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <DotLoader accessibleLabel="" size="small" />
        <Text color="secondary" type="b100">{label}</Text>
      </div>
    </div>
  );
};
